import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  OutlineConvert,
  OutlineRefreshCircle,
} from "../../../../../Icon";

export const OfflineSyncIndicator = ({
  isOnline,
  isSyncing,
  pendingCount,
  onSync,
  disabled = false,
}) => {
  const theme = useTheme();
  const [t] = useTranslation("investigation");

  // Don't show if online and no pending items
  if (isOnline && pendingCount === 0) return null;

  return (
    <Collapse in={!isOnline || pendingCount > 0}>
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 1,
          mb: 1.5,
          backgroundColor: !isOnline
            ? theme.palette.warning.light
            : theme.palette.info.light,
          borderRadius: 2,
          border: `1px solid ${
            !isOnline ? theme.palette.warning.main : theme.palette.info.main
          }`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {/* Status icon */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isSyncing ? (
              <CircularProgress size={20} color="inherit" />
            ) : !isOnline ? (
              <OutlineConvert color={theme.palette.warning.dark} />
            ) : (
              <OutlineRefreshCircle color={theme.palette.info.dark} />
            )}
          </Box>

          {/* Status text */}
          <Box>
            <Typography
              variant="body2"
              fontWeight={600}
              color="white"
              sx={{
                textShadow: `1px 1px 1px ${
                  !isOnline
                    ? theme.palette.warning.dark
                    : theme.palette.info.dark
                }`,
              }}
            >
              {!isOnline
                ? t("Interview.offline_mode")
                : isSyncing
                ? t("Interview.syncing_data")
                : t("Interview.pending_sync")}
            </Typography>
            {pendingCount > 0 && (
              <Typography
                variant="body1"
                fontWeight={600}
                color="white"
                sx={{
                  opacity: 0.8,
                  textShadow: `1px 1px 1px ${
                    !isOnline
                      ? theme.palette.warning.dark
                      : theme.palette.info.dark
                  }`,
                }}
              >
                {pendingCount === 1
                  ? t("Interview.one_item_pending")
                  : t("Interview.items_pending", { count: pendingCount })}
              </Typography>
            )}
          </Box>
        </Stack>

        {/* Actions */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          {/* Pending count badge */}
          {pendingCount > 0 && (
            <Chip
              size="small"
              label={pendingCount}
              color={!isOnline ? "warning" : "info"}
              sx={{ fontWeight: 700, minWidth: 32 }}
            />
          )}

          {/* Sync button (only when online and has pending) */}
          {isOnline && pendingCount > 0 && !isSyncing && (
            <Tooltip title={t("Interview.sync_now")}>
              <IconButton
                size="small"
                onClick={onSync}
                disabled={disabled || isSyncing}
                sx={{ color: theme.palette.info.dark }}
              >
                <Box
                  component="span"
                  className="material-symbols-outlined"
                  sx={{ fontSize: 20 }}
                >
                  sync
                </Box>
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Paper>
    </Collapse>
  );
};

OfflineSyncIndicator.displayName = "OfflineSyncIndicator";
